import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AutoForwardJob, AutoForwardRule } from '../../../models/auto-forwarding.model';
import { AutoForwardingService } from '../../../services/auto-forwarding.service';
import {
  ConfigureAutoForwardDialogComponent,
  ConfigureAutoForwardDialogData,
} from '../configure-auto-forward-dialog/configure-auto-forward-dialog.component';

@Component({
  selector: 'app-auto-forwarding-section',
  templateUrl: './auto-forwarding-section.component.html',
  styleUrls: ['./auto-forwarding-section.component.scss'],
  standalone: false,
})
export class AutoForwardingSectionComponent implements OnInit {
  @Input() caseName!: string;
  @Input() hubFacilityId!: string;

  loading = true;
  rule: AutoForwardRule | null = null;
  recentJobs: AutoForwardJob[] = [];
  failedJobs: AutoForwardJob[] = [];
  destinationFacilityName = '';
  retryingJobId: string | null = null;
  disabling = false;
  error = '';

  get sentCount(): number {
    return this.recentJobs.filter(j => j.status === 'sent').length;
  }
  get failedCount(): number {
    return this.recentJobs.filter(j => j.status === 'failed').length;
  }

  constructor(
    private autoForwardingService: AutoForwardingService,
    private dialog: MatDialog
  ) {}

  async ngOnInit(): Promise<void> {
    await this.reload();
  }

  async reload(): Promise<void> {
    this.loading = true;
    this.error = '';
    try {
      [this.rule, this.recentJobs] = await Promise.all([
        this.autoForwardingService.getRule(this.hubFacilityId, this.caseName),
        this.autoForwardingService.getRecentJobs(this.hubFacilityId, this.caseName),
      ]);
      this.failedJobs = this.recentJobs.filter(j => j.status === 'failed');
      if (this.rule?.destinationSpokeId) {
        this.destinationFacilityName = await this.autoForwardingService.getFacilityName(
          this.rule.destinationSpokeId
        );
      }
    } catch (err) {
      console.error('[AutoForwardingSection] load error', err);
      this.error = 'Failed to load auto-forwarding status.';
    } finally {
      this.loading = false;
    }
  }

  openConfigureDialog(): void {
    const data: ConfigureAutoForwardDialogData = {
      caseName: this.caseName,
      hubFacilityId: this.hubFacilityId,
      existingRule: this.rule,
    };
    this.dialog
      .open(ConfigureAutoForwardDialogComponent, { width: '480px', disableClose: true, data })
      .afterClosed()
      .subscribe(saved => {
        if (saved) this.reload();
      });
  }

  async onDisable(): Promise<void> {
    if (!this.rule || this.disabling) return;
    this.disabling = true;
    try {
      await this.autoForwardingService.disableRule(this.hubFacilityId, this.caseName);
      await this.reload();
    } catch (err) {
      console.error('[AutoForwardingSection] disable error', err);
      this.error = 'Failed to disable rule. Please try again.';
    } finally {
      this.disabling = false;
    }
  }

  async onRetry(job: AutoForwardJob): Promise<void> {
    if (this.retryingJobId) return;
    this.retryingJobId = job.id;
    try {
      await this.autoForwardingService.retryJob(this.hubFacilityId, job.id);
      await this.reload();
    } catch (err) {
      console.error('[AutoForwardingSection] retry error', err);
      this.error = 'Failed to queue retry. Please try again.';
    } finally {
      this.retryingJobId = null;
    }
  }

  truncateUid(uid: string): string {
    return uid?.length > 14 ? uid.substring(0, 14) + '…' : uid;
  }
}
